import React, { useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import "@/App.css";
import { ThemeProvider } from "@/lib/theme";
import { AuthProvider, useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import CommandPalette from "@/components/CommandPalette";
import CommandDeck from "@/components/CommandDeck";
import TaskBar from "@/components/TaskBar";
import Dashboard from "@/pages/Dashboard";
import MonthView from "@/pages/MonthView";
import WeeklyOS from "@/pages/WeeklyOS";
import DealPipeline from "@/pages/DealPipeline";
import RealEstateMedia from "@/pages/RealEstateMedia";
import TabPage from "@/pages/TabPage";
import CalendarSync from "@/pages/CalendarSync";
import Team from "@/pages/Team";
import Briefing from "@/pages/Briefing";
import Dossier from "@/pages/Dossier";
import Settings from "@/pages/Settings";
import SignIn from "@/pages/SignIn";
import SignUp from "@/pages/SignUp";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Billing from "@/pages/Billing";
import Admin from "@/pages/Admin";
import Landing from "@/pages/Landing";
import Pricing from "@/pages/Pricing";
import WeekBoard from "@/pages/WeekBoard";
import FocusMode from "@/pages/FocusMode";
import { InstallPrompt } from "@/components/InstallPrompt";
import { PinLock } from "@/components/PinLock";
import ErrorBoundary from "@/components/ErrorBoundary";

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center mono text-muted-foreground uppercase tracking-widest text-xs">
        Authenticating…
      </div>
    );
  }
  if (user === false) {
    const next = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/signin?next=${next}`} replace />;
  }
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role === "admin") return children;
  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  return null;
}

function AppShell() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [deckOpen, setDeckOpen] = useState(false);
  const { user } = useAuth();
  const authed = user && user !== false;

  return (
    <>
      <Routes>
        <Route path="/signin" element={authed ? <Navigate to="/" replace /> : <SignIn />} />
        <Route path="/signup" element={authed ? <Navigate to="/" replace /> : <SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/landing" element={<Landing />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route
          path="/"
          element={
            authed ? (
              <Protected>
                <Layout
                  onCommandOpen={() => setPaletteOpen(true)}
                  onDeckOpen={() => setDeckOpen(true)}
                >
                  <Dashboard />
                </Layout>
              </Protected>
            ) : user === null ? (
              <div className="min-h-screen flex items-center justify-center mono text-muted-foreground uppercase tracking-widest text-xs">
                Authenticating…
              </div>
            ) : (
              <Landing />
            )
          }
        />
        <Route
          path="/*"
          element={
            <Protected>
              <Layout
                onCommandOpen={() => setPaletteOpen(true)}
                onDeckOpen={() => setDeckOpen(true)}
              >
                <Routes>
                  <Route path="/month" element={<MonthView />} />
                  <Route path="/weekly" element={<WeeklyOS />} />
                  <Route path="/board" element={<WeekBoard />} />
                  <Route path="/focus/:taskId" element={<FocusMode />} />
                  <Route path="/deals" element={<DealPipeline />} />
                  <Route path="/media" element={<RealEstateMedia />} />
                  <Route path="/tab/:tabId" element={<TabPage />} />
                  <Route path="/calendar" element={<CalendarSync />} />
                  <Route path="/team" element={<Team />} />
                  <Route path="/briefing" element={<Briefing />} />
                  <Route path="/dossier" element={<Dossier />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/billing" element={<Billing />} />
                  <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
                </Routes>
              </Layout>
            </Protected>
          }
        />
      </Routes>
      {authed && (
        <>
          <TaskBar />
          <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
          <CommandDeck open={deckOpen} onClose={() => setDeckOpen(false)} />
          <PinLock />
        </>
      )}
      <InstallPrompt />
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <ThemeProvider>
          <BrowserRouter>
            <AuthProvider>
              <AppShell />
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </ErrorBoundary>
    </div>
  );
}
